import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CreditCard, CheckCircle2, AlertTriangle, Clock, ExternalLink, FileText, Download } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  es: {
    trialing: 'En prueba gratuita',
    active: 'Activa',
    past_due: 'Pago pendiente',
    canceled: 'Cancelada'
  },
  en: {
    trialing: 'Free trial',
    active: 'Active',
    past_due: 'Payment overdue',
    canceled: 'Canceled'
  }
};

const STATUS_COLORS = {
  trialing: 'bg-blue-100 text-blue-700',
  active: 'bg-emerald-100 text-emerald-700',
  past_due: 'bg-amber-100 text-amber-700',
  canceled: 'bg-red-100 text-red-700'
};

const Billing = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');

  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/billing/status`);
      setStatus(response.data);
      if (response.data?.has_stripe_customer) {
        try {
          const invoicesRes = await axios.get(`${API_URL}/billing/invoices`);
          setInvoices(invoicesRes.data || []);
        } catch (err) {
          console.error('Error fetching invoices:', err);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || (isEs ? 'No se pudo cargar el estado de facturación' : 'Could not load billing status'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const daysLeft = (isoDate) => {
    if (!isoDate) return null;
    const diff = new Date(isoDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const handleCheckout = async () => {
    setError('');
    setActionLoading(true);
    try {
      const response = await axios.post(`${API_URL}/billing/checkout-session`);
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || (isEs ? 'Error al iniciar el pago' : 'Error starting checkout'));
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setError('');
    setActionLoading(true);
    try {
      const response = await axios.post(`${API_URL}/billing/portal-session`);
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || (isEs ? 'Error al abrir el portal de facturación' : 'Error opening billing portal'));
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const remainingTrialDays = status?.subscription_status === 'trialing' ? daysLeft(status.trial_ends_at) : null;

  let pastDueDaysLeft = null;
  if (status?.subscription_status === 'past_due' && status.past_due_since) {
    const deadline = new Date(status.past_due_since).getTime() + (status.past_due_grace_days || 7) * 24 * 60 * 60 * 1000;
    pastDueDaysLeft = Math.max(0, Math.ceil((deadline - Date.now()) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="billing-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
          {isEs ? 'Facturación' : 'Billing'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isEs ? 'Gestiona la suscripción de tu empresa.' : "Manage your company's subscription."}
        </p>
      </div>

      {checkoutResult === 'success' && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          {isEs ? 'Pago procesado. Puede tardar unos segundos en reflejarse.' : 'Payment processed. It may take a few seconds to reflect here.'}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {pastDueDaysLeft !== null && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm" data-testid="billing-past-due-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {pastDueDaysLeft > 0
            ? (isEs
                ? `No hemos podido cobrar tu último pago. Actualiza tu método de pago en los próximos ${pastDueDaysLeft} día(s) para no perder el acceso.`
                : `We couldn't charge your last payment. Update your payment method within the next ${pastDueDaysLeft} day(s) to avoid losing access.`)
            : (isEs
                ? 'El plazo para regularizar el pago ha terminado. Actualiza tu método de pago para recuperar el acceso.'
                : 'The grace period to fix your payment has ended. Update your payment method to regain access.')}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{status?.plan?.display_name || '-'}</CardTitle>
            <Badge className={STATUS_COLORS[status?.subscription_status] || 'bg-slate-100 text-slate-700'}>
              {STATUS_LABELS[language]?.[status?.subscription_status] || status?.subscription_status}
            </Badge>
          </div>
          <CardDescription>
            {status?.plan?.price_monthly != null && `€${status.plan.price_monthly}/${isEs ? 'mes' : 'mo'}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {remainingTrialDays !== null && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4" />
              {isEs
                ? `Quedan ${remainingTrialDays} día(s) de prueba gratuita.`
                : `${remainingTrialDays} day(s) left in your free trial.`}
            </div>
          )}

          {status?.current_period_end && (
            <div className="text-sm text-slate-600">
              {isEs ? 'Próxima renovación: ' : 'Next renewal: '}
              {new Date(status.current_period_end).toLocaleDateString(isEs ? 'es-ES' : 'en-US')}
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              onClick={handleCheckout}
              disabled={actionLoading}
              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              data-testid="billing-checkout-btn"
            >
              <CreditCard className="w-4 h-4" />
              {status?.has_stripe_customer
                ? (isEs ? 'Cambiar de plan' : 'Change plan')
                : (isEs ? 'Suscribirse' : 'Subscribe')}
            </Button>

            {status?.has_stripe_customer && (
              <Button
                variant="outline"
                onClick={handlePortal}
                disabled={actionLoading}
                className="gap-2"
                data-testid="billing-portal-btn"
              >
                <ExternalLink className="w-4 h-4" />
                {isEs ? 'Gestionar facturación' : 'Manage billing'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {status?.has_stripe_customer && invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-400" />
              {isEs ? 'Facturas' : 'Invoices'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isEs ? 'Fecha' : 'Date'}</TableHead>
                  <TableHead>{isEs ? 'Importe' : 'Amount'}</TableHead>
                  <TableHead>{isEs ? 'Estado' : 'Status'}</TableHead>
                  <TableHead className="text-right">{isEs ? 'Descarga' : 'Download'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{new Date(inv.created).toLocaleDateString(isEs ? 'es-ES' : 'en-US')}</TableCell>
                    <TableCell>{inv.amount_paid.toFixed(2)} {inv.currency?.toUpperCase()}</TableCell>
                    <TableCell>
                      <Badge className={inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                        {inv.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" data-testid={`invoice-download-${inv.id}`}>
                          <Button variant="ghost" size="sm" className="gap-1.5">
                            <Download className="w-4 h-4" />
                            PDF
                          </Button>
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Billing;
